// src/pages/admin/CoachForm.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import API from '../../api';
import AddressFields from '../../components/common/address/AddressFields';
import { addressPayloadFields, hasAnyAddress, validateAddressIfPresent } from '../../components/common/address/addressValidators';
import DatePicker from '../../components/common/DatePicker';
import { formatDate, parseRegionalDate, toISODateFromRegional, academyTodayDate } from '../../utils/date';
import PageHeaderSection from '../../components/layout/PageHeaderSection';
import PageLoadingShell from '../../components/layout/PageLoadingShell';
import Button from '../../components/common/Button';
import PhoneNumberFields from '../../components/common/PhoneNumberFields';
import { validateStoredPhone } from '../../utils/phone';
import { getApiErrorInfo } from '../../utils/apiError';
import { showConfirm } from '../../utils/popup';
import { TextInput, SelectInput, CheckboxField, FormField } from '../../components/common/form';
import { isValidPersonName } from '../../utils/name';

const ROLE_CONST = 'COACH';
const ALL_ROLES = ['ADMIN', 'MANAGER', 'COACH', 'STUDENT'];

const CoachForm = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const [initialLoading, setInitialLoading] = useState(isEditMode);
    const [loadError, setLoadError] = useState('');
    const [sendInvite, setSendInvite] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const navigate = useNavigate();

    const [form, setForm] = useState({
        firstName: '',
        middleName: '',
        lastName: '',
        dob: '',
        gender: 'MALE',
        username: '',
        email: '',
        phoneNumber: '',
        active: true,
        sendInvite: true, // create only

        // roles state as array (backend requires at least one role)
        roles: [ROLE_CONST],

        // Address
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        country: '',
        pincode: '',
        latitude: '',
        longitude: '',
    });

    // Robust role extraction for edit mode (backend may return role, roles, userRoles, etc.)
    const extractRoles = (u) => {
        if (!u) return [ROLE_CONST];

        const toName = (r) => {
            if (typeof r === 'string') return r;
            if (!r) return ROLE_CONST;
            return r.name || r.role || r.code || ROLE_CONST;
        };

        if (Array.isArray(u.roles) && u.roles.length) return u.roles.map(toName);
        if (Array.isArray(u.userRoles) && u.userRoles.length) return u.userRoles.map(toName);
        if (u.role) return [toName(u.role)];

        return [ROLE_CONST];
    };

    const setField = (name, value) => setForm((p) => ({ ...p, [name]: value }));

    useEffect(() => {
        if (!isEditMode) return;
        setInitialLoading(true);
        API.get(`/admin/users/${id}`)
            .then((res) => {
                const u = res.data || {};
                const a = u.address || {};
                setForm({
                    firstName: u.firstName || u.first_name || '',
                    middleName: u.middleName || u.middle_name || '',
                    lastName: u.lastName || u.last_name || '',
                    dob: u.dob || '',
                    gender: u.gender || 'MALE',
                    username: u.username || '',
                    email: u.email || '',
                    phoneNumber: u.phoneNumber || u.phone_number || '',
                    active: u.active !== false,
                    sendInvite: false, // not used in edit
                    roles: extractRoles(u),

                    addressId: u.address?.id ?? null,
                    addressLine1: a.addressLine1 || '',
                    addressLine2: a.addressLine2 || '',
                    city: a.city || '',
                    state: a.state || '',
                    country: a.country || '',
                    pincode: a.pincode || '',
                    latitude: a.latitude != null ? String(a.latitude) : '',
                    longitude: a.longitude != null ? String(a.longitude) : '',
                });
            })
            .catch((err) => {
                console.error('Error fetching coach:', err);
                setLoadError(t('common.listLoadFailed'));
            })
            .finally(() => setInitialLoading(false));
    }, [id, isEditMode, t]);

    // Roles UI (chips), same pattern as Manager/Coach
    const RoleChip = ({ role }) => {
        const active = (form.roles || []).includes(role);
        return (
            <button
                type="button"
                onClick={() => {
                    setForm((f) => {
                        const has = (f.roles || []).includes(role);
                        const next = has
                            ? (f.roles || []).filter((x) => x !== role)
                            : [...(f.roles || []), role];
                        return { ...f, roles: next };
                    });
                }}
                className={active ? 'role-chip role-chip-active' : 'role-chip'}
            >
                {t(`faq.roles.${role}`, { defaultValue: role })}
            </button>
        );
    };

    // Validations
    const validate = () => {
        if (!form.firstName || !isValidPersonName(form.firstName)) {
            alert(t('forms.validation.firstNameRequired'));
            return false;
        }
        if (form.middleName && !isValidPersonName(form.middleName)) {
            alert(t('forms.validation.middleNameLetters'));
            return false;
        }
        if (!form.lastName || !isValidPersonName(form.lastName)) {
            alert(t('forms.validation.lastNameRequired'));
            return false;
        }

        if (form.dob) {
            const d = parseRegionalDate(form.dob);
            const today = academyTodayDate();
            if (d >= today) {
                alert(t('forms.validation.invalidDob'));
                return false;
            }
        }

        if (!isEditMode) {
            if (!form.username || form.username.trim().length === 0) {
                alert(t('forms.validation.usernameRequired'));
                return false;
            }
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!form.email || !emailRegex.test(form.email)) {
                alert(t('forms.validation.emailInvalid'));
                return false;
            }
        }

        const phoneCheck = validateStoredPhone(form.phoneNumber);
        if (!phoneCheck.valid) {
            alert(phoneCheck.message);
            return false;
        }

        if (!isEditMode && !form.sendInvite) {
            if (!password || password.length < 6) {
                alert(t('forms.validation.passwordMin'));
                return false;
            }
            if (password !== confirmPassword) {
                alert(t('forms.validation.passwordsMismatch'));
                return false;
            }
        }


        // Must have at least one role
        if (!form.roles || !Array.isArray(form.roles) || form.roles.length === 0) {
            alert(t('forms.validation.rolesRequired'));
            return false;
        }

        // Address: optional; validate only if present using shared validators
        const addressObj = {
            addressLine1: form.addressLine1,
            addressLine2: form.addressLine2,
            city: form.city,
            state: form.state,
            country: form.country,
            pincode: form.pincode,
            latitude: form.latitude,
            longitude: form.longitude,
        };

        const addressPresent = hasAnyAddress(addressObj);
        if (addressPresent) {
            const v = validateAddressIfPresent(addressObj);
            if (v && v.ok === false) {
                alert(v.message || t('forms.validation.invalidAddress'));
                return false;
            }
            if (form.latitude && isNaN(parseFloat(form.latitude))) {
                alert(t('forms.validation.invalidLatitude'));
                return false;
            }
            if (form.longitude && isNaN(parseFloat(form.longitude))) {
                alert(t('forms.validation.invalidLongitude'));
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const rolesPayload =
            Array.isArray(form.roles) && form.roles.length ? form.roles : [ROLE_CONST];

        const addressObj = {
            addressLine1: form.addressLine1,
            addressLine2: form.addressLine2,
            city: form.city,
            state: form.state,
            country: form.country,
            pincode: form.pincode,
            latitude: form.latitude,
            longitude: form.longitude,
        };

        const payload = {
            firstName: form.firstName,
            middleName: form.middleName,
            lastName: form.lastName,
            dob: toISODateFromRegional(form.dob) || null,
            gender: form.gender,
            username: form.username,
            email: form.email,
            phoneNumber: validateStoredPhone(form.phoneNumber).normalized,
            active: form.active,
            roles: rolesPayload, // multi-role support

            ...addressPayloadFields(addressObj, { addressId: form.addressId }),

            ...(isEditMode
                ? {}
                : form.sendInvite
                    ? { sendInvite: true }
                    : { sendInvite: false, password }
            ),

        };

        try {
            if (isEditMode) {
                await API.put(`/admin/users/${id}`, payload);
            } else {
                await API.post('/admin/users', payload);
            }
            alert(isEditMode ? t('forms.coach.updated') : (form.sendInvite ? t('accountAccess.signInLink.success') : t('forms.coach.created')));
            navigate('/admin/coaches', { state: { fromForm: Date.now() } });
        } catch (err) {
            console.error('Error saving coach:', err);
            const errorInfo = getApiErrorInfo(
                err,
                isEditMode ? t('forms.coach.updateFailed') : t('forms.coach.saveFailed'),
            );
            alert(`${errorInfo.title}\n\n${errorInfo.message}`);
        }
    };

    if (initialLoading) {
        return (
            <PageLoadingShell
                variant="page-shell-form"
                title={isEditMode ? t('forms.coach.editTitle') : t('forms.coach.addTitle')}
                subtitle={t('forms.coach.subtitle')}
            />
        );
    }

    if (isEditMode && loadError) {
        return (
            <div className="page-shell-form">
                <PageHeaderSection
                    title={t('forms.coach.editTitle')}
                    subtitle={t('forms.coach.subtitle')}
                />
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="status">
                    {loadError}
                </div>
                <button type="button" className="btn-page-action-secondary mt-4" onClick={() => navigate('/admin/coaches')}>
                    {t('common.back')}
                </button>
            </div>
        );
    }

    return (
        <div className="page-shell-form">
            <PageHeaderSection
                title={isEditMode ? t('forms.coach.editTitle') : t('forms.coach.addTitle')}
                subtitle={t('forms.coach.subtitle')}
            />

            <form onSubmit={handleSubmit} className="form-shell">
                {/* Names */}
                <div className="form-section grid grid-cols-1 gap-3 md:grid-cols-3">
                    <TextInput
                        label={t('forms.common.firstName')}
                        name="firstName"
                        value={form.firstName}
                        onChange={(e) => setField('firstName', e.target.value)}
                        placeholder={t('forms.placeholders.firstNameRequired')}
                        required
                    />
                    <TextInput
                        label={t('forms.common.middleName')}
                        name="middleName"
                        value={form.middleName}
                        onChange={(e) => setField('middleName', e.target.value)}
                        placeholder={t('forms.placeholders.middleName')}
                    />
                    <TextInput
                        label={t('forms.common.lastName')}
                        name="lastName"
                        value={form.lastName}
                        onChange={(e) => setField('lastName', e.target.value)}
                        placeholder={t('forms.placeholders.lastNameRequired')}
                        required
                    />
                </div>

                {/* DOB & Gender */}
                <div className="form-section grid grid-cols-1 gap-3 md:grid-cols-2">
                    <FormField label={t('forms.common.dateOfBirth')}>
                        <DatePicker value={formatDate(form.dob)} onChange={(e) => setField('dob', e)}
                            className="w-full"
                        />
                    </FormField>
                    <SelectInput
                        label={t('forms.common.gender')}
                        name="gender"
                        value={form.gender}
                        onChange={(e) => setField('gender', e.target.value)}
                        required
                    >
                        <option value="MALE">{t('forms.common.male')}</option>
                        <option value="FEMALE">{t('forms.common.female')}</option>
                        <option value="OTHER">{t('forms.common.other')}</option>
                    </SelectInput>
                </div>

                {/* Username (create editable, edit disabled) */}
                <div className="form-section">
                    <TextInput
                        label={t('forms.common.loginUsername')}
                        name="username"
                        value={form.username}
                        onChange={(e) => setField('username', e.target.value)}
                        muted={isEditMode}
                        required
                        disabled={isEditMode}
                    />
                    <TextInput
                        label={t('forms.common.emailAddress')}
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={(e) => setField('email', e.target.value)}
                        muted={isEditMode}
                        required={!isEditMode}
                        disabled={isEditMode}
                    />
                </div>

                {/* Phone & Active */}
                <div className="form-section grid grid-cols-1 gap-3 md:grid-cols-2">
                    <FormField label={t('forms.common.phoneNumber')} required>
                        <PhoneNumberFields
                            value={form.phoneNumber}
                            onChange={(v) => setField('phoneNumber', v)}
                            required
                        />
                    </FormField>
                    <CheckboxField
                        layout="toggle-card"
                        className="mt-6 md:mt-0"
                        label={t('forms.common.accountActive')}
                        description={t('forms.coach.activeDesc')}
                        name="active"
                        checked={form.active}
                        onChange={(e) => setField('active', e.target.checked)}
                    />
                </div>

                {/* Roles (multi-select) */}
                <div className="form-section">
                    <div className="form-section-title">{t('forms.common.roles')}</div>
                    <div className="flex flex-wrap gap-2">
                        {ALL_ROLES.map((r) => (
                            <RoleChip key={r} role={r} />
                        ))}
                    </div>
                </div>

                {/* Invite (create only) */}
                {!isEditMode && (
                    <div className="form-section">
                        <CheckboxField
                            label={t('accountAccess.signInLink.createCheckbox')}
                            checked={form.sendInvite}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                setSendInvite(checked);
                                setField('sendInvite', checked);

                                if (checked) {
                                    setPassword('');
                                    setConfirmPassword('');
                                }
                            }}
                        />
                        {form.sendInvite && (
                            <p className="field-note">{t('accountAccess.signInLink.createNote')}</p>
                        )}
                    </div>
                )}

                {!isEditMode && !form.sendInvite && (
                    <div className="form-section">
                        <TextInput
                            label={t('forms.common.loginPassword')}
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                        <TextInput
                            label={t('forms.common.confirmLoginPassword')}
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                        />
                    </div>
                )}


                {/* Address (optional dropdowns via AddressFields) */}
                <AddressFields
                    value={form}
                    applyDefaultCountry={!isEditMode}
                    onChange={(patchOrName, maybeValue) => {
                        if (typeof patchOrName === 'string') {
                            setField(patchOrName, maybeValue);
                        } else if (patchOrName && typeof patchOrName === 'object') {
                            setForm((prev) => ({ ...prev, ...patchOrName }));
                        }
                    }}
                />

                {/* Actions */}
                <div className="page-actions justify-end pt-2">
                    <Button type="submit" variant="gradient-primary">
                        {isEditMode ? t('forms.coach.updateButton') : t('forms.coach.createButton')}
                    </Button>

                    {isEditMode && (
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={async () => {
                                if (!await showConfirm(t('accountAccess.signInLink.editConfirm', { role: t('userActions.labels.coach') }))) return;
                                try {
                                    await API.post(`/admin/users/${id}/invite`);
                                    alert(t('accountAccess.signInLink.success'));
                                } catch (e) {
                                    console.error(e);
                                    alert(t('accountAccess.signInLink.failure'));
                                }
                            }}
                        >
                            {t('accountAccess.signInLink.button')}
                        </Button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default CoachForm;